import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API = "https://api18081113.queroapp.pl";

export default function LicenseGuard({ children }) {
    const [status, setStatus] = useState("loading"); // loading, valid, invalid
    const [inputCode, setInputCode] = useState("");
    const [error, setError] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [trialExpired, setTrialExpired] = useState(false);
    const [trialEndsAt, setTrialEndsAt] = useState(null);
    const navigate = useNavigate();

    const secretId = localStorage.getItem("secretId") ||
        localStorage.getItem("secredId") ||
        localStorage.getItem("secret-id") ||
        localStorage.getItem("secred-id");

    const checkLicense = async () => {
        if (!secretId) {
            // No secretId -> probably not logged in or invalid state.
            // Let the underlying component handle redirect or redirect here.
            // For now, assume if no secretId, we can't check license, so valid? or redirect?
            // Existing flow redirects in AdminDashboard if no secretId.
            // Let's pass through and let AdminDashboard handle "no secretId" redirect.
            setStatus("valid");
            return;
        }

        try {
            const res = await fetch(`${API}/verify-auth-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ secretId }),
            });
            const data = await res.json();

            if (data.success) {
                setStatus("valid");
                if (data.trial_ends_at) {
                    setTrialEndsAt(data.trial_ends_at);
                    const isTrialActive = new Date(data.trial_ends_at) > new Date();
                    localStorage.setItem(`isTrial_${secretId}`, isTrialActive ? "true" : "false");
                } else {
                    localStorage.setItem(`isTrial_${secretId}`, "false");
                }
            } else {
                setStatus("invalid");
                if (data.trial_expired) {
                    setTrialExpired(true);
                }
            }
        } catch (err) {
            console.error("License check error:", err);
            // On network error, maybe block access or retry? 
            // Blocking is safer for "guard".
            setStatus("invalid");
        }
    };

    useEffect(() => {
        checkLicense();
        // Setup polling every minute to re-check
        const interval = setInterval(() => {
            checkLicense();
        }, 60000);

        return () => clearInterval(interval);
    }, [secretId]);

    // Effect to handle trial expiration timer if trial_ends_at is provided
    useEffect(() => {
        if (status === 'valid' && trialEndsAt) {
            const endDate = new Date(trialEndsAt).getTime();
            const now = new Date().getTime();
            const timeRemaining = endDate - now;

            if (timeRemaining <= 0) {
                // Already expired
                setTrialExpired(true);
                setStatus("invalid");
            } else {
                // Set timer to expire exact moment
                const timer = setTimeout(() => {
                    setTrialExpired(true);
                    setStatus("invalid");
                }, timeRemaining);
                return () => clearTimeout(timer);
            }
        }
    }, [status, trialEndsAt]);

    const handleActivate = async (e) => {
        e.preventDefault();
        setVerifying(true);
        setError("");

        try {
            // 1. Update the code
            const updateRes = await fetch(`${API}/update-auth-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ secretId, auth_code: inputCode }),
            });
            const updateData = await updateRes.json();

            if (!updateData.success) {
                setError(updateData.message || "Błąd aktualizacji kodu.");
                setVerifying(false);
                return;
            }

            // 2. Verify again
            await checkLicense();
            // checkLicense updates 'status' state.

        } catch (err) {
            setError("Wystąpił błąd połączenia.");
        } finally {
            setVerifying(false); // checkLicense is async but we don't wait for it here if it updates state appropriately? 
            // Actually checkLicense is async and updates state. We should be fine.
        }
    };

    // --- RENDER ---

    // Blur applied if NOT valid (loading or invalid)
    const isBlurred = status !== "valid";

    const blurStyle = {
        filter: isBlurred ? "blur(8px)" : "none",
        pointerEvents: isBlurred ? "none" : "auto",
        userSelect: isBlurred ? "none" : "auto",
        transition: "filter 0.3s ease",
        height: "100%",
        width: "100%",
        overflow: isBlurred ? "hidden" : "unset" // Prevent scrolling when blocked
    };

    return (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>

            {/* Main Content (Blurred if not valid) */}
            <div style={blurStyle}>
                {children}
            </div>

            {/* Overlay for Loading or Invalid */}
            {isBlurred && (
                <div
                    style={{
                        position: "fixed", // Fixed to cover everything
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                        backgroundColor: "rgba(255,255,255,0.3)" // Light overlay to help with contrast
                    }}
                >
                    {status === "loading" && (
                        <div className="bg-white p-4 rounded-4 shadow text-center">
                            <div className="spinner-border text-primary mb-3" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <h5 className="mb-0 fw-bold text-dark">Weryfikacja licencji...</h5>
                        </div>
                    )}

                    {status === "invalid" && (
                        <div className="bg-white p-5 rounded-4 shadow-lg text-center border-0 animate-pop-in" style={{ maxWidth: "480px", width: "90%" }}>
                            <div className="mb-4 text-primary bg-primary-subtle d-inline-block p-4 rounded-circle">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" className="bi bi-key-fill" viewBox="0 0 16 16">
                                    <path d="M3.5 11.5a3.5 3.5 0 1 1 3.163-5H14L15.5 8 14 9.5l-1-1-1 1-1-1-1 1-1-1-1 1H6.663a3.5 3.5 0 0 1-3.163 2zM2.5 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
                                </svg>
                            </div>
                            <h2 className="fw-bold mb-2">
                                {trialExpired ? "Twój okres próbny minął" : "Aktywacja Wydarzenia"}
                            </h2>
                            <p className="text-muted mb-4">
                                {trialExpired
                                    ? "Aby korzystać dalej, wprowadź kod licencyjny lub wykup pakiet."
                                    : "Wprowadź 8-cyfrowy kod autoryzacyjny, aby odblokować panel zarządzania."}
                            </p>

                            <form onSubmit={handleActivate}>
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className="form-control form-control-lg text-center fw-bold letter-spacing-4 fs-4 py-3"
                                        placeholder="00000000"
                                        value={inputCode}
                                        onChange={(e) => {
                                            // Allow only numbers? optional
                                            setInputCode(e.target.value);
                                        }}
                                        maxLength={8}
                                        style={{ letterSpacing: '0.2em' }}
                                    />
                                </div>

                                {error && (
                                    <div className="alert alert-danger py-2 text-sm mb-3 border-0 bg-danger-subtle text-danger fw-bold">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="btn btn-primary w-100 btn-lg rounded-pill fw-bold py-3 mt-2 shadow-sm hover-scale"
                                    disabled={verifying || inputCode.length < 8}
                                >
                                    {verifying ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Weryfikacja...
                                        </>
                                    ) : "AKTYWUJ DOSTĘP"}
                                </button>

                                <div className="mt-4 pt-2 border-top">
                                    <p className="small text-muted mb-2">
                                        {trialExpired ? "Chcesz przedłużyć dostęp?" : "Nie masz kodu?"}
                                    </p>
                                    <a href="/packages" className="btn btn-link text-decoration-none fw-bold p-0 text-primary hover-underline">
                                        {trialExpired ? "Wykup dostęp teraz" : "Sprawdź pakiety i uzyskaj dostęp"}
                                    </a>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            )}

            <style>{`
                .animate-pop-in { animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
                @keyframes popIn {
                    from { opacity: 0; transform: scale(0.95) translateY(10px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
                .hover-scale:hover { transform: scale(1.02); }
                .hover-underline:hover { text-decoration: underline!important; }
                .letter-spacing-4 { letter-spacing: 4px; }
            `}</style>
        </div>
    );
}
